import React, { useCallback, useEffect, useState } from 'react';
import SidebarToggle from './SidebarToggle';

const CHAT_SIDEBAR_STORAGE_KEY = 'creyon_chat_sidebar_open';
const CHAT_DESKTOP_QUERY = '(min-width: 769px)';

function isDesktopViewport() {
  return typeof window !== 'undefined' && window.matchMedia(CHAT_DESKTOP_QUERY).matches;
}

interface ChatLayoutProps {
  sidebar: React.ReactNode;
  children: React.ReactNode;
}

const ChatLayout: React.FC<ChatLayoutProps> = ({ sidebar, children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(CHAT_SIDEBAR_STORAGE_KEY);
      if (isDesktopViewport()) return true;
      if (stored === 'false') return false;
      if (stored === 'true') return true;
      return isDesktopViewport();
    } catch {
      return true;
    }
  });

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((current) => !current);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_SIDEBAR_STORAGE_KEY, String(isSidebarOpen));
    } catch {
      // Ignore storage failures in private browsing or locked-down environments.
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia(CHAT_DESKTOP_QUERY);
    const handleViewportChange = () => {
      if (desktopQuery.matches) {
        setIsSidebarOpen(true);
      }
    };

    desktopQuery.addEventListener('change', handleViewportChange);
    return () => desktopQuery.removeEventListener('change', handleViewportChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSidebar();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeSidebar]);

  return (
    <div className={`chat-layout ${isSidebarOpen ? 'chat-layout--sidebar-open' : ''}`}>
      <div className="chat-sidebar-shell">
        {sidebar}
      </div>
      <SidebarToggle isOpen={isSidebarOpen} onToggle={toggleSidebar} />
      <button
        type="button"
        className="chat-sidebar-backdrop"
        aria-label="Close chat sidebar"
        onClick={closeSidebar}
      />
      <div className="chat-layout__main">
        {children}
      </div>
    </div>
  );
};

export default ChatLayout;
